 
import styled, { css, keyframes } from 'styled-components';
import rem from 'polished/lib/helpers/rem';
// template components
import { Container, Row, Col } from '../../foundations/Grid';
import Typography from '../../foundations/Typography';
import { getSectionBackgroundColor } from '../../components/SectionContainer/styles';

// helper
const sharpenBg = keyframes`
  0% {
    -webkit-filter: blur(${rem(20)});
    filter: blur(${rem(20)});
  }
  100% {
    -webkit-filter: blur(0);
    filter: blur(0);
  }
`;

// start styled components
export const ColStyled = styled(Col)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      ${!mediumUp && 'padding: 0;'};
    `;
  }
}`;

export const ContainerStyled = styled(Container)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      ${!mediumUp && 'padding: 0;'};
      z-index: 1;
      margin: 0;
      ${mediumUp && 'height: 100%;'};
    `;
  }
}`;

export const DivStyledBackgroundContainer = styled.div`
  ${({
    desktopImage,
    desktopImageJpg, // deprecated
    desktopImageLowRes,
    desktopImageWebp, // deprecated
    imageLoaded,
    mobileImage,
    mobileImageJpg, // deprecated
    mobileImageLowRes,
    mobileImageWebp, // deprecated
    theme,
  }) => {
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();
    let bgImageHeight = rem(120);
    let thisBackgroundImageDesktop;
    let thisBackgroundImageMobile;

    if (mediumUp) {
      bgImageHeight = '100%;';
    } else if (smallUp) {
      bgImageHeight = rem(160);
    }

    if (!imageLoaded) {
      thisBackgroundImageDesktop = desktopImageLowRes;
      thisBackgroundImageMobile = mobileImageLowRes;
    }

    // the following logic will be simplified in the next major version, once the deprecated props are removed
    // prioritize the desktopImage prop first, if passed in. If not, assign the appropriate WEBP or JPG image based on screen size
    if (desktopImage) {
      thisBackgroundImageDesktop = desktopImage;
    } else if (desktopImageWebp) {
      thisBackgroundImageDesktop = desktopImageWebp;
      console.warn('desktopImageWebp is deprecated for the FlexibleHero module. Please use desktopImage instead.');
    } else if (desktopImageJpg) {
      thisBackgroundImageDesktop = desktopImageJpg;
      console.warn('desktopImageJpg is deprecated for the FlexibleHero module. Please use desktopImage instead.');
    } else {
      console.warn('A desktop image prop is required for the FlexibleHero module. Please use desktopImage.');
    }

    // prioritize the mobileImage prop first, if passed in. If not, assign the appropriate WEBP or JPG image based on screen size
    if (mobileImage) {
      thisBackgroundImageMobile = mobileImage;
    } else if (mobileImageWebp) {
      thisBackgroundImageMobile = mobileImageWebp;
      console.warn('mobileImageWebp is deprecated for the FlexibleHero module. Please use mobileImage instead.');
    } else if (mobileImageJpg) {
      thisBackgroundImageMobile = mobileImageJpg;
      console.warn('mobileImageJpg is deprecated for the FlexibleHero module. Please use mobileImage instead.');
    } else {
      console.warn('A mobile image prop is required for the FlexibleHero module. Please use mobileImage.');
    }

    return css`
      ${mediumUp && `
        position: absolute;
        top: 0;
        left: 0;
      `};
      width: 100%;
      height: ${bgImageHeight};
      background: ${theme.colors.background.lightTheme.heroLazyLoad} no-repeat scroll 50% 20%;
      background-position: center;
      background-image: url(${mediumUp ? desktopImageLowRes : mobileImageLowRes});
      background-image: url(${mediumUp ? thisBackgroundImageDesktop : thisBackgroundImageMobile});
      background-size: cover;
      transform: scale(1);
      -webkit-animation: ${sharpenBg} 1s both;
      animation: ${sharpenBg} 1s both;
      @media print {
        && {
          display: none !important;
        }
      }
    `;
  }
}`;

// gray box
export const DivStyledHeadingContainer = styled.div`
  ${({
    backgroundVariant,
    branding,
    headingTextPosition,
    theme,
    themeType,
  }) => {
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();
    let padding = `${rem(18)} ${rem(24)} ${rem(10)}}`;
    let marginBottom = 0;
    let justifyContent = 'center';

    if (largeUp) {
      padding = `${rem(26)} ${rem(48)} ${rem(16)}}`;
    }

    if (headingTextPosition === 'bottom') {
      if (largeUp) {
        marginBottom = rem(60);
      } else if (mediumUp) {
        marginBottom = rem(48);
      }
    }

    if (mediumUp && headingTextPosition === 'left') {
      justifyContent = 'flex-start';
    }

    return css`
      background-color: ${getSectionBackgroundColor(backgroundVariant, branding, theme, themeType)};
      padding: ${padding};
      margin-bottom: ${marginBottom};
      height: ${largeUp ? 'auto' : '100%'};
      display: flex;
      align-items: center;
      justify-content: ${justifyContent};
    `;
  }}
`;

export const RowStyled = styled(Row)`
  ${({ theme, headingTextPosition }) => {
    const mediumUp = theme.mediumUp();
    let verticalPosition = 'flex-end';

    if (mediumUp) {
      if (headingTextPosition === 'left' || headingTextPosition === 'center') {
        verticalPosition = 'center';
      }
    }

    return css`
      height: 100%;
      text-align: left;
      justify-content: left;
      align-items: ${verticalPosition};
    `;
  }
}
`;

export const SectionStyled = styled.section`
  ${({ theme }) => {
    const xxlargeUp = theme.xxlargeUp();
    const xlargeUp = theme.xlargeUp();
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();
    let height = 'auto';

    if (xxlargeUp) {
      height = rem(660);
    } else if (xlargeUp) {
      height = rem(495);
    } else if (largeUp) {
      height = rem(350);
    } else if (mediumUp) {
      height = rem(265);
    }

    return css`
      overflow: hidden;
      padding-top: 0;
      padding-bottom: 0;
      position: relative;
      align-items: center;
      background-color: ${theme.colors.neutral.white};
      color: ${theme.colors.neutral.grayDark20};
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      height: ${height};
      text-align: center;
      padding: 0;
    `;
  }
}`;

export const TypographyStyled = styled(Typography)`
  ${({ theme, headingTextPosition }) => {
    const xlargeUp = theme.xlargeUp();
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();
    let fontSize = rem(26);
    let lineHeight = rem(32);
    const paddingBottom = rem(16);
    let textAlign = 'center';
    let margin = '0 auto';

    if (xlargeUp) {
      fontSize = rem(44);
      lineHeight = rem(52);
    } else if (largeUp) {
      fontSize = rem(36);
      lineHeight = rem(36);
    } else if (mediumUp) {
      fontSize = rem(26);
      lineHeight = rem(26);
    }

    if (headingTextPosition === 'left' && mediumUp) {
      textAlign = 'left';
      margin = 0;
    }

    return css`
      && {
        font-family: 'Guardian Sans', 'Helvetica Neue', Helvetica, sans-serif;
        padding-bottom: ${paddingBottom};
        line-height: ${lineHeight};
        font-size: ${fontSize};
        text-align: ${textAlign};
        ::after {
          display: block;
          width: ${rem(60)};
          padding-bottom: ${paddingBottom};
          border-bottom: ${rem(4)} solid ${theme.colors.gold.gold};
          content: '';
          text-align: ${textAlign};
          margin: ${margin};
        }
      }
    `;
  }}
`;
